/**
 * WHO THE SIGNED-IN USER ACTUALLY IS — the one identity source for the SPA.
 *
 * GET /api/whoami → { id, email, displayName, roles, isAdmin, features }
 *
 * WHY THIS EXISTS. Two live bugs came from the app not having this:
 *
 *   1. THE RAIL SHOWED MODULES THE USER HAD NO ACCESS TO. ShellLayout read roles
 *      from `idTokenClaims.roles`, but this platform deliberately keeps the JWT
 *      IDENTITY-ONLY — roles are never minted into the token (rbac-model.json:
 *      "The JWT stays IDENTITY-ONLY. Roles are NEVER read from the token"). So the
 *      claim was ALWAYS undefined, gatePlatformNav always took its fail-open
 *      branch, and every user saw every module. Observed 2026-08-10: Stacie was
 *      granted CRM + Hotel Supplier and still saw Chat and Expense.
 *
 *   2. THE CRM GREETED EVERYONE AS "Dev User". features/crm/pages/home/page.tsx
 *      read `import.meta.env.VITE_MOCK_USER_NAME || 'Dev User'` — a dev
 *      placeholder that is set in no build. Worse than the greeting: that same
 *      string drove the "my book" filters via an exact name comparison against
 *      cust_ae / cust_csm, so every "mine" count on the page was empty by
 *      construction and looked like data.
 *
 * Both needed the same thing: the server's answer to "who am I and what do I
 * hold". Roles come from `public.get_user_roles()` (SECURITY DEFINER, past RLS),
 * so this is the authoritative set — the same rows the surfaces enforce on.
 *
 * NOT A SECURITY BOUNDARY. Every surface still enforces server-side. This drives
 * affordances: which rail items to show, whose book is "mine".
 */
import { api } from '@andavo/web-ui/api';
import { useQuery } from '@tanstack/react-query';

export type Whoami = {
  id: string;
  email: string | null;
  displayName: string | null;
  /** Stored role strings from experts.user_roles (e.g. ['crm:csm']). */
  roles: string[];
  isAdmin: boolean;
  /**
   * Client-visible feature flags: `{ [flagName]: boolean }`. The server returns
   * ONLY the names on its hardcoded allowlist (apps/experts/lib/
   * client-feature-flags.js), fail-closed. Optional because an older server
   * omits it — read it through useFeatureFlag(), which treats absent as `{}`.
   */
  features?: Record<string, boolean>;
};

export const whoamiQuery = {
  queryKey: ['whoami'] as const,
  queryFn: () => api.get<Whoami>('api/whoami'),
  // Identity changes rarely within a session, but a role grant should land
  // without a full sign-out — same short-staleness contract as /api/admin/me.
  staleTime: 60_000,
  retry: 1,
};

export function useWhoami() {
  return useQuery(whoamiQuery);
}

/**
 * The roles to gate the rail on, or `undefined` while unknown.
 *
 * `undefined` is load-bearing: gatePlatformNav treats it as "cannot tell" and
 * fails OPEN, which is correct for a cosmetic nav — showing a module the user
 * can't use is a dead end, but HIDING one they can is a support ticket. So during
 * the initial fetch, and on error, the rail shows everything rather than guessing
 * a smaller set.
 */
export function useStoredRoles(): string[] | undefined {
  const { data, isSuccess } = useWhoami();
  return isSuccess ? data.roles : undefined;
}

/**
 * Is the client-visible feature flag `name` on? FAIL-CLOSED: `true` only when
 * /api/whoami has succeeded AND `features[name]` is exactly boolean `true`.
 * While loading, on error, on an older server with no `features`, for a missing
 * key, or for any non-boolean value, the answer is `false`.
 *
 * This is the OPPOSITE posture to useStoredRoles above, on purpose. The rail
 * fails open because hiding a module someone holds is worse than a dead end; a
 * dark-launched surface fails closed because showing unfinished work to people
 * it was not enabled for is the failure. Like the rail, this is an affordance —
 * every API behind a flagged surface still enforces server-side.
 *
 * Only names on the server's allowlist ever come back (today:
 * 'commissions.workbench.enabled'); asking for any other name is always false.
 */
export function useFeatureFlag(name: string): boolean {
  const { data, isSuccess } = useWhoami();
  if (!isSuccess) return false;
  const features = data.features ?? {};
  if (typeof features !== 'object' || features === null) return false;
  if (!Object.prototype.hasOwnProperty.call(features, name)) return false;
  return features[name] === true;
}

/**
 * The signed-in person's display name, best available. Falls back to the email's
 * local part, then a neutral 'there' for greetings — never a fake name like
 * "Dev User", which is indistinguishable from real data to the person reading it.
 */
export function displayNameFrom(who: Whoami | undefined): string | undefined {
  if (!who) return undefined;
  if (who.displayName) return who.displayName;
  const local = (who.email ?? '').split('@')[0];
  if (!local) return undefined;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}
